import { useSearchParams } from 'react-router'
import { Button, Grid, Tooltip, Typography, useTheme } from '@mui/material'
import { formatDistanceToNow } from 'date-fns'

import { alertSeveritySortOrder } from '../../routes/accounts/components/devices-tab-panel/helpers'
import useSharedStyles from '../../utils/shared.styles'

import AlertSeverityIcon from './alert-severity-icon'
import BatteryIndicatorBubble from './battery-indicator-bubble'
import DefrouteBubble from './defroute-bubble'
import useStyles from './index.styles'
import LeakBubble from './leak-bubble'
import Nh3Bubble from './nh3-bubble'
import Nh3WarningBubble from './nh3-warning-bubble'
import NumberBubble from './number-bubble'
import PowersourceBubble from './powersource-bubble'
import SignalIndicatorBubble from './signal-indicator-bubble'
import ValvePositionBubble from './valve-position-bubble'

const channelChartMatrix: Record<string, string> = {
  signal_indicator: 'signal',
  battery_indicator: 'battery',
  temp: 'temp',
  probe_temp: 'temp',
  probe: 'temp',
  hum: 'temp',
  leak: 'leak',
  leak_probe: 'leakprobe',
  valve: 'valve',
  nh3: 'ammonia',
  warning_nh3: 'ammoniawarning',
  powersource: 'powersource',
}

interface IProps {
  channel: IValueType | undefined
  channelName: string
  label: string
  alerts: IAlertHistory[]
  isOffline: boolean
  nodeType?: {
    id: number
    name: string
    vanityName: string
    properties: string[]
    allowedUsecases: DeviceUseCaseType[]
  }
  defrouteChannel?: IValueType
  channelTooltip?: string
}

const ChannelBubble = ({ channel, channelName, label, alerts, isOffline, nodeType, defrouteChannel, channelTooltip }: IProps) => {
  const theme = useTheme()
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const [, setSearchParams] = useSearchParams()

  let channelValue = channel?.value
  if (channel && ['temp', 'probe_temp', 'probe', 'hum'].includes(channelName) && !channel.ts) {
    channelValue = null
  }

  const alert = alerts
    .filter((a) => a.channel === channelName)
    .sort((a, b) => {
      let alertStatusA = 'none'
      if (a.tags.includes('info')) alertStatusA = 'info'
      if (a.tags.includes('warning')) alertStatusA = 'warning'
      if (a.tags.includes('urgent')) alertStatusA = 'urgent'
      let alertStatusB = 'none'
      if (b.tags.includes('info')) alertStatusB = 'info'
      if (b.tags.includes('warning')) alertStatusB = 'warning'
      if (b.tags.includes('urgent')) alertStatusB = 'urgent'
      if (alertSeveritySortOrder[alertStatusA] < alertSeveritySortOrder[alertStatusB]) return -1
      return 1
    })[0]

  let response = ''
  if (alert?.tags.includes('acknowledged')) response = 'acknowledged'
  if (alert?.tags.includes('declined')) response = 'declined'
  if (alert?.tags.includes('ignored')) response = 'ignored'

  const hasResponse = response !== ''

  const alertSeverity = alert?.tags.find((tag) => ['info', 'warning', 'urgent'].includes(tag)) ?? 'unknown'

  let { borderColor, backgroundColor } = {
    info: { borderColor: theme.palette.info.main, backgroundColor: theme.palette.primary.shades['4p'] },
    warning: { borderColor: theme.palette.warning.light, backgroundColor: theme.palette.warning.lightShades['4p'] },
    urgent: { borderColor: theme.palette.error.main, backgroundColor: theme.palette.error.shades['12p'] },
  }[alertSeverity] ?? { borderColor: theme.palette.divider, backgroundColor: 'transparent' }

  // if the user responded, show the bubble as transparent to avoid drawing attention
  if (hasResponse) backgroundColor = 'transparent'

  // if the device is offline, show the bubble as "inactive"
  if (isOffline) {
    borderColor = theme.palette.divider
    backgroundColor = theme.palette.action.hover
  }

  if (!channel) return null

  // only show the defroute if plugged into ethernet, otherwise the signal bubble will show
  if (channelName === 'defroute' && channelValue !== 'eth0') return null
  if (channelName === 'signal_indicator' && defrouteChannel?.value === 'eth0') return null

  // do not show Power Source for bvs
  if (channelName === 'powersource' && nodeType && (nodeType.name === 'bvs' || nodeType.name === 'bvs2')) return null

  return (
    <Grid
      sx={{
        width: '44px',
      }}
    >
      <Button
        aria-label={`View ${channelName} chart`}
        onClick={() => {
          setSearchParams({ tab: 'charts' })
          const chartName = channelChartMatrix[channelName]
          const chartId = chartName ? `chart_${chartName}` : ''
          if (chartId) {
            setTimeout(() => {
              const chart = document?.getElementById(`${chartId}`)
              chart?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
            }, 100)
          }
        }}
        disableRipple={true}
        sx={{
          '&:hover': {
            background: 'transparent',
          },
        }}
      >
        <Tooltip
          title={
            alert?.ruleName ? (
              <div style={classes.channelTooltipContent}>
                {response === 'acknowledged' && '[ACKNOWLEDGED] '}
                {response === 'declined' && '[DECLINED] '}
                {response === 'ignored' && '[IGNORED] '}
                {alert.ruleName}
                <br />
                {formatDistanceToNow(new Date(alert.updatedAt))} ago
              </div>
            ) : (
              channelTooltip || null
            )
          }
        >
          <div>
            <Grid container={true} sx={[sharedClasses.centerContent, classes.channelBubble, { border: `1px solid ${borderColor}`, backgroundColor }]}>
              {channelName === 'signal_indicator' && (
                <SignalIndicatorBubble value={channelValue as number} isOffline={isOffline} nodeTypeName={nodeType?.name} />
              )}
              {channelName === 'battery_indicator' && <BatteryIndicatorBubble value={channelValue as number} isOffline={isOffline} />}

              {channelName === 'temp' && <NumberBubble value={channelValue as string} isOffline={isOffline} />}
              {channelName === 'probe_temp' && <NumberBubble value={channelValue as string} isOffline={isOffline} />}
              {channelName === 'probe' && <NumberBubble value={channelValue as string} isOffline={isOffline} />}
              {channelName === 'hum' && <NumberBubble value={channelValue as string} isOffline={isOffline} />}
              {channelName === 'water_pressure' && <NumberBubble value={channelValue as string} isOffline={isOffline} />}

              {channelName === 'leak' && <LeakBubble value={channelValue as number} isOffline={isOffline} />}
              {channelName === 'leak_probe' && <LeakBubble value={channelValue as number} isOffline={isOffline} />}

              {channelName === 'valve' && <ValvePositionBubble value={channelValue as string} isOffline={isOffline} />}

              {channelName === 'nh3' && <Nh3Bubble value={channelValue as number} isOffline={isOffline} />}
              {channelName === 'warning_nh3' && <Nh3WarningBubble value={channelValue as number} isOffline={isOffline} />}

              {channelName === 'powersource' && <PowersourceBubble value={channelValue as string} isOffline={isOffline} />}
              {channelName === 'defroute' && <DefrouteBubble isOffline={isOffline} />}

              <AlertSeverityIcon severity={alertSeverity} response={response} isOffline={isOffline} />
            </Grid>
            <Grid sx={classes.labelContainer}>
              <Typography variant="caption" color="textSecondary" sx={classes.label}>
                {label}
              </Typography>
            </Grid>
          </div>
        </Tooltip>
      </Button>
    </Grid>
  )
}

export default ChannelBubble
